import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { BookIdea } from './useBookIdeas';

export type PenNameProfile = {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  website: string | null;
  social_links: Record<string, string>;
  created_at: string;
  book_ideas?: BookIdea[];
};

export function usePenNameProfile(penNameId: string) {
  const [profile, setProfile] = useState<PenNameProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (penNameId) {
      fetchProfile();
    }
  }, [penNameId]);

  async function fetchProfile() {
    try {
      setLoading(true);
      setError(null);

      // First try to get the pen name profile
      const { data: penName, error: penNameError } = await supabase
        .from('pen_names')
        .select(`
          id,
          username,
          full_name,
          avatar_url,
          bio,
          website,
          social_links,
          created_at
        `)
        .eq('id', penNameId)
        .single();

      if (penNameError) {
        if (penNameError.code === 'PGRST116') {
          setError('Profile not found');
        } else {
          throw penNameError;
        }
        return;
      }

      // Get book ideas associated with this pen name
      const { data: ideasData, error: ideasError } = await supabase
        .from('book_ideas')
        .select(`
          *,
          votes:votes(count),
          comments:comments(count),
          followers:idea_follows(count)
        `)
        .eq('identity_id', penNameId)
        .order('created_at', { ascending: false });

      if (ideasError) throw ideasError;

      const bookIdeas = ideasData.map(idea => ({
        ...idea,
        votes_count: idea.votes[0]?.count ?? 0,
        comments_count: idea.comments[0]?.count ?? 0,
        followers_count: idea.followers[0]?.count ?? 0
      }));

      setProfile({
        ...penName,
        book_ideas: bookIdeas
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  }

  return { profile, loading, error };
}